import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, ChevronLeft, ChevronRight, Loader2, CheckCircle2,
  Edit3, RefreshCw, Calendar, X, Sparkles, MoreVertical, Trash2
} from 'lucide-react';
import EmptyState from '../components/EmptyState';

const CHANNEL_FILTERS = [
  { value: 'all', label: 'Tutti' },
  { value: 'linkedin_personal', label: 'LinkedIn pers.' },
  { value: 'linkedin_company', label: 'LinkedIn az.' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'email', label: 'Email' },
];

const STATUS_MAP = {
  approved: { icon: '\u2705', label: 'Approvato' },
  draft: { icon: '\uD83D\uDCDD', label: 'Bozza' },
  pending: { icon: '\u2B1C', label: 'Da generare' },
  scheduled: { icon: '\uD83D\uDCEC', label: 'Programmato' },
  configuring: { icon: '\u2B1C', label: 'Da generare' },
};

const TYPE_COLORS = {
  post: '#22C55E',
  newsletter: '#EC4899',
  email: '#8B5CF6',
  telegram: '#3B82F6',
  instagram: '#F59E0B',
};

function TagInput({ tags, onChange, placeholder, pillColor }) {
  const [input, setInput] = useState('');
  const add = (t) => { if (t && !tags.includes(t)) onChange([...tags, t]); setInput(''); };
  const remove = (t) => onChange(tags.filter(x => x !== t));
  return (
    <div className="flex flex-wrap gap-1 p-1.5 rounded-md border border-input bg-background min-h-[36px] items-center">
      {tags.map(t => (
        <span key={t} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium text-white"
          style={{ backgroundColor: pillColor || '#64748B' }}>
          {t}<button onClick={() => remove(t)} className="hover:opacity-70"><X className="w-3 h-3" /></button>
        </span>
      ))}
      <input className="flex-1 min-w-[80px] bg-transparent text-xs outline-none placeholder:text-muted-foreground px-1"
        value={input} onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && input.trim()) { e.preventDefault(); add(input.trim()); } }}
        placeholder={tags.length === 0 ? placeholder : ''} />
    </div>
  );
}

function getWeekDates(weekOffset) {
  const now = new Date();
  const day = now.getDay();
  const mondayDiff = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayDiff + (weekOffset * 7));
  const days = [];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven'];
  for (let i = 0; i < 5; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push({
      date: d.toISOString().split('T')[0],
      dayName: dayNames[i],
      dayNum: d.getDate(),
      month: d.getMonth() + 1,
    });
  }
  return days;
}

function getWeekLabel(weekOffset) {
  const days = getWeekDates(weekOffset);
  if (days.length < 5) return '';
  const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
  return `${days[0].dayNum}-${days[4].dayNum} ${months[days[4].month - 1]}`;
}

function getWeekNumber(weekOffset) {
  const days = getWeekDates(weekOffset);
  const d = new Date(days[0].date);
  const startOfYear = new Date(d.getFullYear(), 0, 1);
  const diff = d - startOfYear;
  return Math.ceil((diff / 86400000 + startOfYear.getDay() + 1) / 7);
}

export default function PlannerPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('calendar');
  const [weekOffset, setWeekOffset] = useState(0);
  const [channelFilter, setChannelFilter] = useState('all');
  const [calendarData, setCalendarData] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDropdown, setAddDropdown] = useState(null);

  // Batch generation
  const [batchSeqId, setBatchSeqId] = useState(null);
  const [batchThemes, setBatchThemes] = useState([]);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchResults, setBatchResults] = useState([]);

  // New sequence dialog
  const [showNewSeq, setShowNewSeq] = useState(false);
  const [objectives, setObjectives] = useState([]);
  const [targets, setTargets] = useState([]);
  const [structures, setStructures] = useState([]);
  const [rubriche, setRubriche] = useState([]);
  const [events, setEvents] = useState([]);
  const [channels, setChannels] = useState([]);
  const [seqForm, setSeqForm] = useState({
    name: '', objective_id: '', target_ids: [], rubrica_id: '',
    structure_id: '', total_posts: 3, channel_id: '', event_id: '',
    notes_for_copywriter: '', keywords_required: [], scheduled_dates: [],
  });
  const [creating, setCreating] = useState(false);

  const loadCalendar = useCallback(async () => {
    const wn = getWeekNumber(weekOffset);
    const year = new Date().getFullYear();
    try {
      const res = await api.get(`/planner/calendar?week=${wn}&year=${year}`);
      setCalendarData(res.data?.items || []);
    } catch { setCalendarData([]); }
  }, [weekOffset]);

  const loadSequences = useCallback(async () => {
    try {
      const res = await api.get('/planner/sequences');
      setSequences(res.data || []);
    } catch { setSequences([]); }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const [objR, tgtR, strR, rubR, evtR, chR] = await Promise.all([
        api.get('/content-config/objectives'),
        api.get('/content-config/targets'),
        api.get('/content-config/structures'),
        api.get('/content/rubriche'),
        api.get('/events?status=open'),
        api.get('/social-profiles'),
      ]);
      setObjectives(objR.data || []);
      setTargets(tgtR.data || []);
      setStructures(strR.data || []);
      setRubriche(rubR.data || []);
      setEvents(evtR.data || []);
      setChannels((chR.data || []).filter(c => c.buffer_profile_id));
    } catch {}
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadCalendar(), loadSequences(), loadConfig()]).finally(() => setLoading(false));
  }, [loadCalendar, loadSequences, loadConfig]);

  useEffect(() => { loadCalendar(); }, [weekOffset, loadCalendar]);

  // Calendar helpers
  const weekDates = getWeekDates(weekOffset);

  const getItemsForDate = (dateStr) => {
    let items = calendarData.filter(item => {
      const itemDate = item.date || item.scheduled_date || '';
      return itemDate.startsWith(dateStr);
    });
    if (channelFilter !== 'all') {
      items = items.filter(item => (item.channel || '').toLowerCase().includes(channelFilter));
    }
    return items;
  };

  // Batch generation
  const startBatch = (seq) => {
    const total = seq.total_posts || seq.items?.length || 3;
    const generated = seq.generated_count || seq.items?.filter(i => i.status === 'generated' || i.status === 'approved').length || 0;
    const pending = total - generated;
    setBatchSeqId(seq.id);
    setBatchThemes(Array(pending > 0 ? pending : total).fill(''));
    setBatchResults([]);
  };

  const runBatch = async () => {
    if (batchThemes.some(t => !t.trim())) {
      toast.error('Inserisci il tema per ogni post');
      return;
    }
    setBatchGenerating(true);
    try {
      const res = await api.post(`/planner/sequences/${batchSeqId}/generate-batch`, {
        themes: batchThemes,
      });
      setBatchResults(res.data?.results || res.data || []);
      toast.success(`${batchThemes.length} post generati!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione batch');
    }
    setBatchGenerating(false);
  };

  const approvePost = async (contentId) => {
    try {
      const res = await api.patch(`/approval-queue/${contentId}`, { action: 'approved' });
      const status = res.data?.status || 'approved';
      const undoAction = {
        label: 'Annulla',
        onClick: async () => {
          try {
            await api.patch(`/approval-queue/${contentId}`, { action: 'rejected', rejection_note: 'Annullato' });
            setBatchResults(prev => prev.map(p => p.content_id === contentId ? { ...p, approved: false, status: 'draft' } : p));
            toast.success('Approvazione annullata');
          } catch {}
        },
      };
      if (status === 'sent') {
        toast.success('Post pubblicato!', { duration: 5000 });
      } else if (status === 'rescheduled') {
        toast.success('Post approvato — programmato per il prossimo slot', { action: undoAction, duration: 5000 });
      } else {
        toast.success('Post approvato', { action: undoAction, duration: 5000 });
      }
      setBatchResults(prev => prev.map(p =>
        p.content_id === contentId ? { ...p, approved: true, status } : p
      ));
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore approvazione'); }
  };

  const approveAll = async () => {
    try {
      let sentCount = 0, scheduledCount = 0;
      for (const post of batchResults) {
        if (!post.approved) {
          const res = await api.patch(`/approval-queue/${post.content_id}`, { action: 'approved' });
          if (res.data?.status === 'sent') sentCount++;
          else scheduledCount++;
        }
      }
      const msg = sentCount > 0
        ? `${sentCount} pubblicati, ${scheduledCount} programmati`
        : `${scheduledCount || batchResults.length} post approvati e programmati`;
      toast.success(msg);
      setBatchSeqId(null);
      setBatchResults([]);
      loadSequences();
      loadCalendar();
    } catch { toast.error('Errore approvazione batch'); }
  };

  // New sequence
  const updateSeqDates = (count) => {
    const dates = Array(count).fill('');
    setSeqForm(p => ({ ...p, total_posts: count, scheduled_dates: dates }));
  };

  const createSequence = async () => {
    if (!seqForm.name.trim()) { toast.error('Inserisci un nome'); return; }
    const hasDates = seqForm.scheduled_dates.some(d => d);
    if (!hasDates) {
      toast.warning('Nessuna data inserita — i post non appariranno nel calendario', {
        description: 'Puoi aggiungere le date in seguito.',
      });
    }
    setCreating(true);
    try {
      const res = await api.post('/planner/sequences', {
        name: seqForm.name, objective_id: seqForm.objective_id || undefined,
        target_ids: seqForm.target_ids.length ? seqForm.target_ids : undefined,
        rubrica_id: seqForm.rubrica_id || undefined,
        structure_id: seqForm.structure_id || undefined,
        total_posts: seqForm.total_posts,
        channel_id: seqForm.channel_id || undefined,
        event_id: seqForm.event_id || undefined,
        notes_for_copywriter: seqForm.notes_for_copywriter || undefined,
        keywords_required: seqForm.keywords_required.length ? seqForm.keywords_required : undefined,
        scheduled_dates: seqForm.scheduled_dates.filter(d => d) || undefined,
      });
      const newSeqId = res.data?.id || '';
      toast.success('Sequenza creata', {
        description: `${seqForm.total_posts} post da generare`,
        action: newSeqId ? {
          label: 'Genera nel Content Studio →',
          onClick: () => navigate(`/content-studio?sequence=${newSeqId}`),
        } : undefined,
        duration: 6000,
      });
      setShowNewSeq(false);
      setSeqForm({ name: '', objective_id: '', target_ids: [], rubrica_id: '', structure_id: '', total_posts: 3, channel_id: '', event_id: '', notes_for_copywriter: '', keywords_required: [], scheduled_dates: [] });
      loadSequences();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione sequenza');
    }
    setCreating(false);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="planner-loading">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-warm-accent)' }} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col gap-3" data-testid="planner-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-display)' }}>Planner</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--color-muted)' }}>Pianifica e visualizza i tuoi contenuti</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowNewSeq(true)} data-testid="new-sequence-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Nuova sequenza
          </Button>
          <Button size="sm" onClick={() => navigate('/content-studio')} data-testid="add-content-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi contenuto
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 border-b pb-0" style={{ borderColor: '#E5E7EB' }}>
        {[
          { key: 'calendar', label: 'Calendario settimanale' },
          { key: 'sequences', label: 'Sequenze' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className="text-sm font-medium pb-2 border-b-2 transition-colors px-1"
            style={{
              color: activeTab === tab.key ? '#2563EB' : 'var(--color-muted)',
              borderColor: activeTab === tab.key ? '#2563EB' : 'transparent',
            }}
            data-testid={`tab-${tab.key}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ═══ TAB 1: CALENDARIO ═══ */}
      {activeTab === 'calendar' && (
        <div className="flex-1 flex flex-col gap-3 overflow-hidden">
          {/* Filters */}
          <div className="flex flex-wrap gap-1.5">
            {CHANNEL_FILTERS.map(f => (
              <Badge key={f.value} variant={channelFilter === f.value ? 'default' : 'outline'}
                className="cursor-pointer text-[11px] px-2.5 py-0.5"
                onClick={() => setChannelFilter(f.value)}
                data-testid={`filter-${f.value}`}>{f.label}</Badge>
            ))}
          </div>

          {/* Week navigation */}
          <div className="flex items-center justify-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => setWeekOffset(w => w - 1)} data-testid="prev-week">
              <ChevronLeft className="w-4 h-4" /> Prec.
            </Button>
            <span className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
              Settimana {getWeekLabel(weekOffset)}
            </span>
            <Button variant="ghost" size="sm" onClick={() => setWeekOffset(w => w + 1)} data-testid="next-week">
              Succ. <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Grid */}
          <div className="flex-1 grid grid-cols-5 gap-2 overflow-hidden" data-testid="calendar-grid">
            {weekDates.map(day => {
              const items = getItemsForDate(day.date);
              const isToday = day.date === new Date().toISOString().split('T')[0];
              const isEmpty = items.length === 0;
              return (
                <div key={day.date} className="flex flex-col rounded-lg border overflow-hidden"
                  style={{ borderColor: isToday ? '#3B82F6' : '#E5E7EB', backgroundColor: isEmpty ? '#FFFBF7' : '#FFFFFF' }}
                  data-testid={`day-${day.date}`}>
                  {/* Day header */}
                  <div className="px-3 py-2 text-center border-b" style={{ borderColor: '#E5E7EB', backgroundColor: isToday ? '#EFF6FF' : '#F9FAFB' }}>
                    <p className="text-[11px] font-medium" style={{ color: 'var(--color-muted)' }}>{day.dayName}</p>
                    <p className="text-sm font-bold" style={{ color: isToday ? '#2563EB' : 'var(--color-primary)' }}>{day.dayNum}/{day.month}</p>
                  </div>

                  {/* Items */}
                  <div className="flex-1 p-1.5 space-y-1 overflow-y-auto min-h-[120px]">
                    {items.map((item, i) => {
                      const channelType = (item.type || item.content_type || item.channel || 'post').toLowerCase();
                      const channelColor = channelType.includes('newsletter') ? '#EC4899'
                        : channelType.includes('email') ? '#8B5CF6'
                        : channelType.includes('instagram') ? '#F59E0B'
                        : channelType.includes('telegram') ? '#06B6D4'
                        : '#3B82F6'; // default blue for post/linkedin
                      const statusDotColor = item.status === 'approved' || item.status === 'scheduled'
                        ? '#22C55E' : item.status === 'draft' ? '#F59E0B' : '#D1D5DB';
                      // Urgency: <48h from now
                      const itemDateStr = item.scheduled_date || item.date || '';
                      const itemDate = itemDateStr ? new Date(itemDateStr) : null;
                      const isUrgent = itemDate && (itemDate - Date.now()) < 48 * 60 * 60 * 1000 && (itemDate - Date.now()) > 0
                        && item.status !== 'approved' && item.status !== 'scheduled';
                      return (
                        <button key={item.id || i}
                          onClick={() => item.content_id
                            ? navigate(`/content-studio?id=${item.content_id}`)
                            : item.sequence_id
                              ? navigate(`/content-studio?sequence=${item.sequence_id}&post=${item.post_number || 1}`)
                              : navigate(`/content-studio?date=${day.date}&topic=${encodeURIComponent(item.title || '')}`)
                          }
                          className={`w-full text-left rounded-md p-2 hover:bg-gray-50 transition-colors border-l-[3px] ${isUrgent ? 'animate-pulse' : ''}`}
                          style={{
                            borderLeftColor: channelColor,
                            backgroundColor: '#FFFFFF',
                            border: `1px solid ${isUrgent ? '#F97316' : '#F3F4F6'}`,
                            borderLeftWidth: '3px',
                            borderLeftColor: channelColor,
                          }}
                          data-testid={`item-${item.id || i}`}>
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: statusDotColor }} />
                            <p className="text-[11px] font-medium truncate flex-1" style={{ color: 'var(--color-primary)' }}>
                              {item.title || item.content?.substring(0, 30) || 'Contenuto'}
                            </p>
                          </div>
                          <p className="text-[10px] ml-3.5" style={{ color: 'var(--color-muted)' }}>
                            {item.channel_label || item.channel || ''}
                          </p>
                          {item.sequence_name && (
                            <span className="ml-3.5 text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-medium inline-block mt-0.5">
                              {item.sequence_name}
                            </span>
                          )}
                          {item.scheduled_time && <p className="text-[10px] ml-3.5" style={{ color: 'var(--color-muted)' }}>{item.scheduled_time}</p>}
                        </button>
                      );
                    })}
                  </div>

                  {/* Add button */}
                  <div className="relative px-1.5 pb-1.5">
                    <button onClick={() => setAddDropdown(addDropdown === day.date ? null : day.date)}
                      className="w-full py-1.5 rounded-md text-[11px] font-medium hover:bg-gray-100 transition-colors"
                      style={{ color: 'var(--color-muted)' }}
                      data-testid={`add-${day.date}`}>
                      + Aggiungi
                    </button>
                    {addDropdown === day.date && (
                      <div className="absolute bottom-full left-0 right-0 mb-1 bg-white rounded-lg shadow-lg border z-10">
                        <button className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50"
                          onClick={() => { setAddDropdown(null); navigate(`/content-studio?date=${day.date}`); }}>
                          Nuovo post &rarr;
                        </button>
                        <button className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50"
                          onClick={() => { setAddDropdown(null); navigate(`/content-studio?type=newsletter&date=${day.date}`); }}>
                          Nuova newsletter &rarr;
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 flex-wrap py-1 px-1" data-testid="calendar-legend">
            <span className="text-[10px] font-medium" style={{ color: 'var(--color-muted)' }}>Canali:</span>
            {[
              { label: 'LinkedIn', color: '#3B82F6' },
              { label: 'Newsletter', color: '#EC4899' },
              { label: 'Email', color: '#8B5CF6' },
              { label: 'Instagram', color: '#F59E0B' },
              { label: 'Telegram', color: '#06B6D4' },
            ].map(c => (
              <span key={c.label} className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--color-muted)' }}>
                <span className="w-3 h-1.5 rounded-sm" style={{ backgroundColor: c.color }} /> {c.label}
              </span>
            ))}
            <span className="ml-2 text-[10px] font-medium" style={{ color: 'var(--color-muted)' }}>Status:</span>
            {[
              { label: 'Approvato', color: '#22C55E' },
              { label: 'Bozza', color: '#F59E0B' },
              { label: 'Da generare', color: '#D1D5DB' },
            ].map(s => (
              <span key={s.label} className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--color-muted)' }}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} /> {s.label}
              </span>
            ))}
          </div>
        </div>
      )}
      {activeTab === 'sequences' && (
        <div className="flex-1 overflow-y-auto space-y-3">
          {sequences.length === 0 ? (
            <div className="flex items-center justify-center pt-12">
              <EmptyState type="calendar" title="Nessuna sequenza"
                text="Crea una sequenza editoriale per pianificare i contenuti in modo strutturato." />
            </div>
          ) : sequences.map(seq => {
            const total = seq.total_posts || seq.items?.length || 0;
            const generated = seq.generated_count || seq.items?.filter(i => i.status === 'generated' || i.status === 'approved' || i.content_id).length || 0;
            const progress = total > 0 ? (generated / total) * 100 : 0;
            const isBatchOpen = batchSeqId === seq.id;

            return (
              <Card key={seq.id} className="overflow-hidden" style={{ boxShadow: 'var(--shadow-card)', borderRadius: 'var(--radius-soft)' }}
                data-testid={`sequence-${seq.id}`}>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold flex-1" style={{ color: 'var(--color-primary)' }}>
                      {seq.name || seq.title}
                    </span>
                    <Badge variant={seq.status === 'active' ? 'default' : 'outline'} className="text-[10px]">
                      {seq.status === 'active' ? 'Attiva' : seq.status || 'Pendente'}
                    </Badge>
                    <button onClick={async (e) => {
                      e.stopPropagation();
                      if (!window.confirm(`Eliminare la sequenza "${seq.name || seq.title}"? I post non generati verranno rimossi.`)) return;
                      try {
                        await api.delete(`/planner/sequences/${seq.id}`);
                        toast.success('Sequenza eliminata');
                        loadSequences();
                        loadCalendar();
                      } catch {
                        try {
                          await api.patch(`/planner/sequences/${seq.id}`, { status: 'cancelled' });
                          toast.success('Sequenza annullata');
                          loadSequences();
                          loadCalendar();
                        } catch { toast.error('Errore eliminazione sequenza'); }
                      }
                    }} className="text-gray-400 hover:text-red-500 transition-colors p-1" data-testid={`delete-seq-${seq.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Progress bar */}
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: '#22C55E' }} />
                    </div>
                    <span className="text-[11px] font-medium" style={{ color: 'var(--color-muted)' }}>
                      {generated}/{total} post generati
                    </span>
                  </div>

                  {/* Meta */}
                  <p className="text-[11px]" style={{ color: 'var(--color-muted)' }}>
                    {seq.objective_label || ''} {seq.target_labels?.join(', ') ? `\u00B7 ${seq.target_labels.join(', ')}` : ''}
                    {seq.channel_label ? ` \u00B7 ${seq.channel_label}` : ''}
                    {seq.rubrica_code ? ` \u00B7 ${seq.rubrica_code}` : ''}
                  </p>

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => isBatchOpen ? setBatchSeqId(null) : startBatch(seq)}
                      data-testid={`batch-${seq.id}`}>
                      <Sparkles className="w-3.5 h-3.5 mr-1" />
                      {isBatchOpen ? 'Chiudi' : 'Genera tutti i post'}
                    </Button>
                    <Button size="sm" variant="ghost"
                      onClick={() => {
                        const nextPending = (seq.items || []).find(i => i.status === 'pending');
                        const nextPost = nextPending ? nextPending.post_number : 1;
                        navigate(`/content-studio?sequence=${seq.id}&post=${nextPost}`);
                      }}
                      data-testid={`open-cs-${seq.id}`}>
                      Apri nel Content Studio &rarr;
                    </Button>
                  </div>
                </div>

                {/* Batch panel */}
                {isBatchOpen && batchResults.length === 0 && (
                  <div className="border-t p-4 space-y-3" style={{ backgroundColor: '#F9FAFB' }} data-testid="batch-form">
                    <p className="text-xs font-semibold" style={{ color: 'var(--color-primary)' }}>
                      Generazione batch &mdash; {seq.name || seq.title}
                    </p>
                    <p className="text-[11px]" style={{ color: 'var(--color-muted)' }}>
                      {batchThemes.length} post da generare. Per ognuno serve il TEMA.
                    </p>
                    <div className="space-y-2">
                      {batchThemes.map((theme, i) => {
                        const itemDate = seq.items?.[generated + i]?.scheduled_date || seq.scheduled_dates?.[generated + i];
                        return (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-[11px] font-medium w-20 flex-shrink-0" style={{ color: 'var(--color-muted)' }}>
                              Post {generated + i + 1} {itemDate ? `(${new Date(itemDate).toLocaleDateString('it-CH', { day: 'numeric', month: 'numeric' })})` : ''}:
                            </span>
                            <Input value={theme} onChange={e => setBatchThemes(prev => { const n = [...prev]; n[i] = e.target.value; return n; })}
                              placeholder={`Tema post ${generated + i + 1}`} className="text-xs"
                              data-testid={`batch-theme-${i}`} />
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={runBatch} disabled={batchGenerating} data-testid="run-batch">
                        {batchGenerating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                        Genera tutti e {batchThemes.length}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setBatchSeqId(null)}>Annulla</Button>
                    </div>
                  </div>
                )}

                {/* Batch results */}
                {isBatchOpen && batchResults.length > 0 && (
                  <div className="border-t p-4 space-y-3" style={{ backgroundColor: '#F9FAFB' }} data-testid="batch-results">
                    {batchResults.map((post, i) => {
                      const sc = post.content_score;
                      return (
                        <div key={post.content_id || i} className="rounded-lg border p-3 bg-white" data-testid={`batch-result-${i}`}>
                          <div className="flex items-center gap-2 mb-1.5">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            <span className="text-xs font-semibold">Post {post.post_number || i + 1}/{batchResults.length}</span>
                            {sc && (
                              <span className="text-[11px] font-medium px-1.5 py-0.5 rounded" style={{
                                color: sc.total >= 80 ? '#059669' : sc.total >= 60 ? '#D97706' : '#DC2626',
                                backgroundColor: sc.total >= 80 ? '#D1FAE5' : sc.total >= 60 ? '#FEF3C7' : '#FEE2E2',
                              }}>
                                Score {sc.total}/100 {sc.grade}
                              </span>
                            )}
                          </div>
                          <p className="text-xs leading-relaxed line-clamp-3" style={{ color: '#374151' }}>
                            {post.generated_text?.substring(0, 150)}...
                          </p>
                          <div className="flex gap-2 mt-2">
                            <Button size="sm" variant={post.approved ? 'ghost' : 'default'}
                              disabled={post.approved}
                              onClick={() => approvePost(post.content_id)}
                              data-testid={`approve-${i}`}>
                              <CheckCircle2 className="w-3 h-3 mr-1" /> {post.approved ? 'Approvato' : 'Approva'}
                            </Button>
                            <Button size="sm" variant="ghost"
                              onClick={() => navigate(`/content-studio?id=${post.content_id}`)}>
                              <Edit3 className="w-3 h-3 mr-1" /> Modifica
                            </Button>
                            <Button size="sm" variant="ghost" data-testid={`regen-${i}`}>
                              <RefreshCw className="w-3 h-3 mr-1" /> Rigenera
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                    <Button onClick={approveAll} className="w-full" data-testid="approve-all">
                      <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approva tutti e programma
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* ═══ NEW SEQUENCE DIALOG ═══ */}
      <Dialog open={showNewSeq} onOpenChange={setShowNewSeq}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova sequenza editoriale</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2" data-testid="new-sequence-form">
            {/* Nome */}
            <div>
              <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Nome</label>
              <Input value={seqForm.name} onChange={e => setSeqForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Es: Promozione Workshop 15/4" className="text-xs" data-testid="seq-name" />
            </div>

            {/* Obiettivo */}
            <div>
              <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Obiettivo</label>
              <Select value={seqForm.objective_id} onValueChange={v => setSeqForm(p => ({ ...p, objective_id: v }))}>
                <SelectTrigger className="text-xs h-9" data-testid="seq-objective"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>{objectives.map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Target */}
            <div>
              <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Target</label>
              <div className="flex flex-wrap gap-1.5">
                {targets.map(t => (
                  <Badge key={t.id} variant={seqForm.target_ids.includes(t.id) ? 'default' : 'outline'}
                    className="cursor-pointer text-[11px] px-2 py-0.5"
                    onClick={() => setSeqForm(p => ({
                      ...p, target_ids: p.target_ids.includes(t.id) ? p.target_ids.filter(x => x !== t.id) : [...p.target_ids, t.id]
                    }))}>{t.label}</Badge>
                ))}
              </div>
            </div>

            {/* Rubrica */}
            <div>
              <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Rubrica (opzionale)</label>
              <Select value={seqForm.rubrica_id} onValueChange={v => setSeqForm(p => ({ ...p, rubrica_id: v }))}>
                <SelectTrigger className="text-xs h-9" data-testid="seq-rubrica"><SelectValue placeholder="Rubrica..." /></SelectTrigger>
                <SelectContent>{rubriche.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Struttura */}
            <div>
              <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Struttura (opzionale)</label>
              <Select value={seqForm.structure_id} onValueChange={v => setSeqForm(p => ({ ...p, structure_id: v }))}>
                <SelectTrigger className="text-xs h-9" data-testid="seq-structure"><SelectValue placeholder="Struttura..." /></SelectTrigger>
                <SelectContent>{structures.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[10px] mt-0.5 italic" style={{ color: 'var(--color-muted)' }}>Se vuoto, il Copywriter decide</p>
            </div>

            {/* N° post */}
            <div>
              <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Numero post</label>
              <Input type="number" min={1} max={10} value={seqForm.total_posts}
                onChange={e => updateSeqDates(parseInt(e.target.value) || 1)}
                className="text-xs h-9 w-24" data-testid="seq-num-posts" />
            </div>

            {/* Canale */}
            <div>
              <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Canale</label>
              <Select value={seqForm.channel_id} onValueChange={v => setSeqForm(p => ({ ...p, channel_id: v }))}>
                <SelectTrigger className="text-xs h-9" data-testid="seq-channel"><SelectValue placeholder="Seleziona canale..." /></SelectTrigger>
                <SelectContent>
                  {channels.map(c => (
                    <SelectItem key={c.id} value={c.buffer_profile_id || c.id}>
                      {c.platform || c.service} {c.username ? `(${c.username})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div>
              <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Date</label>
              <div className="space-y-1.5">
                {seqForm.scheduled_dates.map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-[11px] w-14 flex-shrink-0" style={{ color: 'var(--color-muted)' }}>Post {i + 1}:</span>
                    <Input type="date" value={d}
                      onChange={e => setSeqForm(p => {
                        const dates = [...p.scheduled_dates];
                        dates[i] = e.target.value;
                        return { ...p, scheduled_dates: dates };
                      })}
                      className="text-xs h-8" data-testid={`seq-date-${i}`} />
                  </div>
                ))}
              </div>
            </div>

            {/* Evento */}
            {events.length > 0 && (
              <div>
                <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Evento (opzionale)</label>
                <Select value={seqForm.event_id} onValueChange={v => setSeqForm(p => ({ ...p, event_id: v }))}>
                  <SelectTrigger className="text-xs h-9" data-testid="seq-event"><SelectValue placeholder="Collega evento..." /></SelectTrigger>
                  <SelectContent>{events.map(e => <SelectItem key={e.id} value={e.id}>{e.name || e.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            {/* Note Copy */}
            <div>
              <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Note per il Copywriter</label>
              <Textarea value={seqForm.notes_for_copywriter}
                onChange={e => setSeqForm(p => ({ ...p, notes_for_copywriter: e.target.value }))}
                placeholder="Indicazioni per il Copywriter..." rows={2} className="text-xs" data-testid="seq-notes" />
            </div>

            {/* Keywords */}
            <div>
              <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Parole chiave obbligatorie</label>
              <TagInput tags={seqForm.keywords_required}
                onChange={v => setSeqForm(p => ({ ...p, keywords_required: v }))}
                placeholder="Aggiungi keyword..." pillColor="#3B82F6" />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowNewSeq(false)}>Annulla</Button>
              <Button onClick={createSequence} disabled={creating || !seqForm.name.trim()} data-testid="create-sequence-btn">
                {creating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                Crea sequenza
              </Button>
            </div>
            <p className="text-[10px] italic text-center" style={{ color: 'var(--color-muted)' }}>
              La sequenza apparira nel Content Studio. Genera i post manualmente o usa Genera tutti.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
